import type { QueryFunction, UseQueryOptions } from '@tanstack/react-query';
import '@tanstack/react-query';
import type { APIClient } from '../api';

export type Namespace = 'root';
export type AppQueryKey = [Namespace, ...ReadonlyArray<unknown>];

declare module '@tanstack/react-query' {
  interface Register {
    queryKey: AppQueryKey;
  }
}

export interface BuildQueryDescriptorParams<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
> extends Omit<
    UseQueryOptions<TQueryFnData, TError, TData, AppQueryKey>,
    'queryKey' | 'queryFn'
  > {
  namespace?: Namespace;
  queryParams?: Record<string, string>;
  query?: string;
  cacheLifeTime?: number;
  cachedResponsesCount?: number;
}

export function urlToQueryKey(url: string) {
  return url.split('/');
}

export function buildQueryDescriptor(
  url: string,
  client: APIClient,
  params?: BuildQueryDescriptorParams,
): {
  queryKey: AppQueryKey;
  queryFn: QueryFunction<unknown, AppQueryKey>;
  restParams: Omit<
    BuildQueryDescriptorParams,
    'namespace' | 'queryParams'
  >;
} {
  const {
    namespace = 'root',
    queryParams = {},
    query: _query,
    cacheLifeTime: _cacheLifeTime,
    cachedResponsesCount: _cachedResponsesCount,
    ...restParams
  } = params || {};

  const queryFn: QueryFunction<unknown, AppQueryKey> = ({ signal }) =>
    client.request({ url, payload: queryParams, options: { signal } });

  const queryKey: AppQueryKey =
    !queryParams || Object.keys(queryParams).length === 0
      ? [namespace, ...urlToQueryKey(url)]
      : [namespace, ...urlToQueryKey(url), queryParams];

  return { queryKey, queryFn, restParams };
}

export function buildTypedQueryDescriptor<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
>(
  url: string,
  client: APIClient,
  params?: BuildQueryDescriptorParams<TQueryFnData, TError, TData>,
) {
  const {
    namespace = 'root',
    queryParams = {},
    query: _query,
    cacheLifeTime: _cacheLifeTime,
    cachedResponsesCount: _cachedResponsesCount,
    ...restParams
  } = params || {};

  const queryFn: QueryFunction<TQueryFnData, AppQueryKey> = ({ signal }) =>
    client.request({ url, payload: queryParams, options: { signal } }) as Promise<TQueryFnData>;

  const queryKey: AppQueryKey =
    !queryParams || Object.keys(queryParams).length === 0
      ? [namespace, ...urlToQueryKey(url)]
      : [namespace, ...urlToQueryKey(url), queryParams];

  return { queryKey, queryFn, restParams };
}
